// Dept.ts - 完整的部门模型
import SafeUtil from '../../util/SafeUtil';
import BaseEntity from '../base/BaseEntity';
import Filter from '../base/filter/Filter';
import SortFilter from '../base/filter/SortFilter';
import InputFilter from '../base/filter/InputFilter';
import SelectionFilter from '../base/filter/SelectionFilter';
import DateUtil from '../../util/DateUtil';
import Lang from '../global/Lang';
import MessageBoxUtil from '../../util/MessageBoxUtil';

// 部门类型枚举
export enum DeptType {
  COMPANY = 'COMPANY',
  DEPARTMENT = 'DEPARTMENT',
  TEAM = 'TEAM'
}

// 导出部门类型列表（使用国际化）
export const DeptTypeList = [
  { 
    name: Lang.t('department.company', '组织'), 
    value: DeptType.COMPANY, 
    label: Lang.t('department.company', '组织') 
  },
  { 
    name: Lang.t('department.department', '部门'), 
    value: DeptType.DEPARTMENT, 
    label: Lang.t('department.department', '部门') 
  },
  { 
    name: Lang.t('department.team', '团队'), 
    value: DeptType.TEAM, 
    label: Lang.t('department.team', '团队') 
  }
];

// 部门同步状态枚举
export enum DeptSyncStatus {
  PENDING = 'PENDING',
  SYNCING = 'SYNCING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PARTIAL = 'PARTIAL'
}

// 部门同步状态列表
export const DeptSyncStatusList = [
  { 
    name: Lang.t('department.syncPending', '待同步'), 
    value: DeptSyncStatus.PENDING,
    color: '#faad14'
  },
  { 
    name: Lang.t('department.syncing', '同步中'), 
    value: DeptSyncStatus.SYNCING,
    color: '#1890ff'
  },
  { 
    name: Lang.t('department.syncSuccess', '同步成功'), 
    value: DeptSyncStatus.SUCCESS,
    color: '#52c41a'
  },
  { 
    name: Lang.t('department.syncFailed', '同步失败'), 
    value: DeptSyncStatus.FAILED,
    color: '#ff4d4f'
  },
  { 
    name: Lang.t('department.syncPartial', '部分成功'), 
    value: DeptSyncStatus.PARTIAL,
    color: '#fa8c16'
  }
];

// 导出状态选项列表（使用国际化）
export const StatusList = [
  { 
    name: Lang.t('department.enabled', '启用'), 
    value: '1', 
    label: Lang.t('department.enabled', '启用') 
  },
  { 
    name: Lang.t('department.disabled', '禁用'), 
    value: '0', 
    label: Lang.t('department.disabled', '禁用') 
  }
];

export default class Dept extends BaseEntity {
  // API URL
  static URL_LIST = '/api/sys/dept/page';
  static URL_INFO = '/api/sys/dept/info';
  static URL_SAVE = '/api/sys/dept/save';
  static URL_DELETE = '/api/sys/dept/delete';
  static URL_BATCH_DELETE = '/api/sys/dept/batchDelete';
  static URL_DEPT_TYPE = '/api/sys/dept/deptType';
  static URL_DEPT_NAME = '/api/sys/dept/deptName';
  static URL_TREE = '/api/sys/dept/datagrid';
  static URL_SYNC_TRIGGER = '/api/sys/dept/sync'; // 部门同步到目标服务器的接口
  static URL_SYNC_STATUS = '/api/sys/dept/sync/status'; // 同步状态查询
  static URL_SYNC_RETRY = '/api/sys/dept/sync/retry'; // 重试失败的同步

  id: number = 0;
  name: string = "";
  shortName: string = "";
  parentId: number = 0;
  type: string = "";
  orderIndex: number = 0;
  status: any = 1;
  memo: string = "";
  level: number = 1;
  sort: number = 0;
  syncStatus: DeptSyncStatus = DeptSyncStatus.PENDING;
  lastSyncTime: Date | null = null;
  syncError: string | null = null;

  createUser: string = "";
  updateUser: string = "";
  parentName: string = "";
  children: Dept[] = [];

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  // 这个方法会被 Pager 调用以获取列表URL
  getUrlList(): string {
    return Dept.URL_LIST;
  }

  assign(obj: any) {
    super.assign(obj);
    
    // 处理数字类型字段
    if (obj.id !== undefined) {
      const idNum = parseInt(obj.id, 10);
      this.id = isNaN(idNum) ? 0 : idNum;
    } else if (obj.ID !== undefined) {
      const idNum = parseInt(obj.ID, 10);
      this.id = isNaN(idNum) ? 0 : idNum;
    } else if (obj.Id !== undefined) {
      const idNum = parseInt(obj.Id, 10);
      this.id = isNaN(idNum) ? 0 : idNum;
    }
    
    if (obj.sort !== undefined) {
      const sortNum = parseInt(obj.sort, 10);
      this.sort = isNaN(sortNum) ? 0 : sortNum;
    }
    
    // 处理 parentId
    if (obj.parentId !== undefined) {
      const parentIdNum = parseInt(obj.parentId, 10);
      this.parentId = isNaN(parentIdNum) ? 0 : parentIdNum;
    } else if (obj.parentID !== undefined) {
      const parentIdNum = parseInt(obj.parentID, 10);
      this.parentId = isNaN(parentIdNum) ? 0 : parentIdNum;
    } else if (obj.ParentID !== undefined) {
      const parentIdNum = parseInt(obj.ParentID, 10);
      this.parentId = isNaN(parentIdNum) ? 0 : parentIdNum;
    } else if (obj.ParentId !== undefined) {
      const parentIdNum = parseInt(obj.ParentId, 10);
      this.parentId = isNaN(parentIdNum) ? 0 : parentIdNum;
    }
    
    if (obj.orderIndex !== undefined) {
      const orderIndexNum = parseInt(obj.orderIndex, 10);
      this.orderIndex = isNaN(orderIndexNum) ? 0 : orderIndexNum;
    } else if (obj.OrderIndex !== undefined) {
      const orderIndexNum = parseInt(obj.OrderIndex, 10);
      this.orderIndex = isNaN(orderIndexNum) ? 0 : orderIndexNum;
    }
    
    // 处理字符串字段
    if (obj.name !== undefined) {
      this.name = String(obj.name);
    } else if (obj.Name !== undefined) {
      this.name = String(obj.Name);
    }
    
    if (obj.shortName !== undefined) {
      this.shortName = String(obj.shortName);
    } else if (obj.ShortName !== undefined) {
      this.shortName = String(obj.ShortName);
    } else if (obj.Shortname !== undefined) {
      this.shortName = String(obj.Shortname);
    }
    
    if (obj.type !== undefined) {
      this.type = String(obj.type);
    } else if (obj.Type !== undefined) {
      this.type = String(obj.Type);
    }
    
    if (obj.status !== undefined) {
      this.status = obj.status;
    } else if (obj.Status !== undefined) {
      this.status = obj.Status;
    }
    
    if (obj.memo !== undefined) {
      this.memo = String(obj.memo);
    } else if (obj.Memo !== undefined) {
      this.memo = String(obj.Memo);
    }
    
    if (obj.level !== undefined) {
      const levelNum = parseInt(obj.level, 10);
      this.level = isNaN(levelNum) ? 1 : levelNum;
    } else if (obj.Level !== undefined) {
      const levelNum = parseInt(obj.Level, 10);
      this.level = isNaN(levelNum) ? 1 : levelNum;
    }
    
    // 处理同步相关字段
    if (obj.syncStatus !== undefined) {
      this.syncStatus = obj.syncStatus as DeptSyncStatus;
    } else if (obj.SyncStatus !== undefined) {
      this.syncStatus = obj.SyncStatus as DeptSyncStatus;
    }
    
    if (obj.lastSyncTime !== undefined && obj.lastSyncTime !== null) {
      const date = new Date(obj.lastSyncTime);
      this.lastSyncTime = isNaN(date.getTime()) ? null : date;
    } else if (obj.LastSyncTime !== undefined && obj.LastSyncTime !== null) {
      const date = new Date(obj.LastSyncTime);
      this.lastSyncTime = isNaN(date.getTime()) ? null : date;
    }
    
    if (obj.syncError !== undefined) {
      this.syncError = String(obj.syncError);
    } else if (obj.SyncError !== undefined) {
      this.syncError = String(obj.SyncError);
    }
    
    if (obj.createTime !== undefined && obj.createTime !== null) {
      const date = new Date(obj.createTime);
      this.createTime = isNaN(date.getTime()) ? null : date;
    } else if (obj.CreateTime !== undefined && obj.CreateTime !== null) {
      const date = new Date(obj.CreateTime);
      this.createTime = isNaN(date.getTime()) ? null : date;
    } else if (obj.createdAt !== undefined && obj.createdAt !== null) {
      const date = new Date(obj.createdAt);
      this.createTime = isNaN(date.getTime()) ? null : date;
    }
    
    if (obj.updateTime !== undefined && obj.updateTime !== null) {
      const date = new Date(obj.updateTime);
      this.updateTime = isNaN(date.getTime()) ? null : date;
    } else if (obj.UpdateTime !== undefined && obj.UpdateTime !== null) {
      const date = new Date(obj.UpdateTime);
      this.updateTime = isNaN(date.getTime()) ? null : date;
    } else if (obj.updatedAt !== undefined && obj.updatedAt !== null) {
      const date = new Date(obj.updatedAt);
      this.updateTime = isNaN(date.getTime()) ? null : date;
    }
    
    // 处理用户字段
    if (obj.createUser !== undefined) {
      this.createUser = String(obj.createUser);
    } else if (obj.CreateUser !== undefined) {
      this.createUser = String(obj.CreateUser);
    } else if (obj.creator !== undefined) {
      this.createUser = String(obj.creator);
    }
    
    if (obj.updateUser !== undefined) {
      this.updateUser = String(obj.updateUser);
    } else if (obj.UpdateUser !== undefined) {
      this.updateUser = String(obj.UpdateUser);
    } else if (obj.updater !== undefined) {
      this.updateUser = String(obj.updater);
    }
    
    // 处理父部门名称
    if (obj.parentName !== undefined) {
      this.parentName = String(obj.parentName);
    } else if (obj.ParentName !== undefined) {
      this.parentName = String(obj.ParentName);
    }
    
    // 处理子部门
    if (obj.children && Array.isArray(obj.children)) {
      this.children = obj.children.map((child: any) => {
        const dept = new Dept();
        dept.assign(child);
        return dept;
      });
    } else if (obj.Children && Array.isArray(obj.Children)) {
      this.children = obj.Children.map((child: any) => {
        const dept = new Dept();
        dept.assign(child);
        return dept;
      });
    }
  }

  getTAG(): string {
    return 'sys/dept';
  }

  getFilters(): Filter[] {
    return [
      ...super.getFilters(),
      new SortFilter(Lang.t('department.orderIndex', '排序索引'), 'orderIndex'),
      new InputFilter(Lang.t('department.nameSearch', '部门名称'), 'name', Lang.t('department.searchHint', '可按部门名称、部门简称搜索')),
      new InputFilter(Lang.t('department.shortNameSearch', '部门简称'), 'shortName', Lang.t('department.searchHint', '可按部门名称、部门简称搜索')),
      new SelectionFilter(Lang.t('department.typeSearch', '部门类型'), 'type', DeptTypeList),
      new SelectionFilter(Lang.t('department.statusSearch', '状态'), 'status', StatusList),
      new SelectionFilter(Lang.t('department.syncStatus', '同步状态'), 'syncStatus', DeptSyncStatusList)
    ];
  }

  getForm(): any {
    // 构建表单数据
    const form: any = {
      name: this.name,
      shortName: this.shortName,
      parentId: this.parentId || '0',
      type: this.type,
      orderIndex: this.orderIndex,
      memo: this.memo || '',
      status: this.status || '1'
    };
    
    // 只有更新时才发送 id
    if (this.id && this.id > 0) {
      form.id = this.id.toString();
    }
    
    return form;
  }

  // 保存部门信息（创建或更新）
  httpSave(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    let that = this;

    let url = Dept.URL_SAVE;

    this.errorMessage = this.validate();
    if (this.errorMessage) {
      that.defaultErrorHandler(this.errorMessage, errorCallback);
      return;
    }

    const requestData = this.getForm();

    this.httpPost(
      url,
      requestData,
      function (response: any) {
        // 更新当前对象的数据
        if (response && response.data) {
          let deptData = response.data;
          // 如果是标准响应格式 {code: "OK", data: {...}}
          if (response.data.data) {
            deptData = response.data.data;
          }
          that.assign(deptData);
        }
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 删除部门
  httpDelete(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    let that = this;

    // 使用 id 作为删除标识
    this.httpPost(
      Dept.URL_DELETE,
      { id: this.id.toString() },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 批量删除部门
  httpBatchDelete(
    ids: number[],
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    // 批量删除接口 - 传递数字ID数组
    this.httpPost(
      Dept.URL_BATCH_DELETE,
      { ids: ids.map(id => id.toString()) },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 获取部门详情
  httpGetInfo(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    if (!this.id || this.id <= 0) {
      this.defaultErrorHandler(Lang.t('department.idRequired', '部门ID不能为空'), errorCallback);
      return;
    }

    this.httpGet(
      Dept.URL_INFO,
      { id: this.id.toString() },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 获取部门类型列表
  static httpGetDeptTypes(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    // 创建临时实例调用静态方法
    const dept = new Dept();
    dept.httpGet(
      Dept.URL_DEPT_TYPE,
      {},
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 根据ID获取部门名称
  static httpGetDeptName(
    id: string | number,
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    const dept = new Dept();
    const idStr = typeof id === 'number' ? id.toString() : id;
    dept.httpGet(
      Dept.URL_DEPT_NAME,
      { id: idStr },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 获取部门树
  static httpGetTree(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    const dept = new Dept();
    // 使用datagrid接口获取树形数据
    dept.httpGet(
      Dept.URL_TREE,
      {},
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  httpTriggerSync(
    targetServer: string,
    successCallback?: any,
    errorCallback?: any
  ) {
    let that = this;
  
    console.log('触发部门同步到目标服务器:', targetServer);
    
    this.httpPost(
      Dept.URL_SYNC_TRIGGER,
      { targetServer: targetServer },
      function (response: any) {
        console.log('部门同步响应:', response);
        console.log('部门同步响应详细数据:', JSON.stringify(response, null, 2));
        console.log('部门同步响应data:', response.data);
        
        // 修正：检查 response.data.code，不是 response.code
        if (response && response.data && (response.data.code === 200 || response.data.code === 0 || response.data.code === "OK" || response.data.success)) {
          console.log('部门同步成功');
          if (successCallback) {
            // 注意：业务数据在 response.data.data 中
            successCallback(response.data.data || response.data);
          }
        } else {
          // 修正：从 response.data 获取错误信息
          const errorMsg = (response && response.data && (response.data.msg || response.data.message || response.data.Msg)) || 
                          Lang.t('operationFailed');
          console.error('部门同步失败:', errorMsg);
          if (errorCallback) {
            errorCallback(new Error(errorMsg));
          }
        }
      },
      function (error: any) {
        console.error('部门同步请求失败:', error);
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );
  }
  // 查询部门同步状态
  httpGetSyncStatus(
    targetServer: string,
    successCallback?: any,
    errorCallback?: any
  ) {
    let that = this;
    
    this.httpGet(
      Dept.URL_SYNC_STATUS,
      { targetServer: targetServer },
      function (response: any) {
        if (response && (response.code === 200 || response.code === 0 || response.code === "OK")) {
          if (successCallback) {
            successCallback(response.data || response);
          }
        } else {
          const errorMsg = response.msg || response.message || response.Msg || Lang.t('operationFailed');
          if (errorCallback) {
            errorCallback(new Error(errorMsg));
          }
        }
      },
      function (error: any) {
        console.error('查询部门同步状态失败:', error);
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );
  }

  // 重试失败的部门同步
  httpRetrySync(
    targetServer: string,
    deptIds: number[],
    successCallback?: any,
    errorCallback?: any
  ) {
    let that = this;
    
    this.httpPost(
      Dept.URL_SYNC_RETRY,
      { 
        targetServer: targetServer,
        deptIds: deptIds.map(id => id.toString())
      },
      function (response: any) {
        console.log('重试部门同步响应:', response);
        
        if (response && (response.code === 200 || response.code === 0 || response.code === "OK" || response.success)) {
          if (successCallback) {
            successCallback(response.data || response);
          }
        } else {
          const errorMsg = response.msg || response.message || response.Msg || Lang.t('operationFailed');
          if (errorCallback) {
            errorCallback(new Error(errorMsg));
          }
        }
      },
      function (error: any) {
        console.error('重试部门同步请求失败:', error);
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );
  }

  // 验证部门数据
  validate(): string | null {
    if (!this.name || this.name.trim() === '') {
      return Lang.t('department.fullNameRequired', '请输入部门全称');
    }
    if (!this.shortName || this.shortName.trim() === '') {
      return Lang.t('department.shortNameRequired', '请输入部门简称');
    }
    if (this.orderIndex < 0) {
      return Lang.t('department.orderIndexMin', '排序索引不能为负数');
    }
    if (this.orderIndex > 9999) {
      return Lang.t('department.orderIndexRange', '排序索引必须在0-9999之间');
    }
    return null;
  }
  
  // 获取ID
  getId(): number {
    return this.id;
  }
  
  // 设置ID
  setId(id: number): void {
    this.id = id;
  }

  // 获取状态文本
  getStatusText(): string {
    return this.status === '1' 
      ? Lang.t('department.enabled', '启用') 
      : Lang.t('department.disabled', '禁用');
  }

  // 获取同步状态文本
  getSyncStatusText(): string {
    const statusItem = DeptSyncStatusList.find(item => item.value === this.syncStatus);
    return statusItem ? statusItem.name : this.syncStatus;
  }

  // 获取同步状态颜色
  getSyncStatusColor(): string {
    const statusItem = DeptSyncStatusList.find(item => item.value === this.syncStatus);
    return statusItem ? statusItem.color : '#d9d9d9';
  }

  // 获取类型文本
  getTypeText(): string {
    const typeItem = DeptTypeList.find(item => item.value === this.type);
    return typeItem ? typeItem.label : this.type;
  }

  // 判断是否为根部门
  isRoot(): boolean {
    return !this.parentId || this.parentId === 0;
  }

  // 获取完整部门路径
  getFullPath(): string {
    if (this.parentName) {
      return `${this.parentName}/${this.shortName}`;
    }
    return this.shortName;
  }

  // 重写 toString 方法
  toString(): string {
    return this.shortName || this.name || '';
  }

  // 获取格式化后的创建时间
  getCreateTimeFormatted(): string {
    if (this.createTime) {
      return DateUtil.simpleDateTime(this.createTime);
    }
    return '';
  }

  // 获取格式化后的更新时间
  getUpdateTimeFormatted(): string {
    if (this.updateTime) {
      return DateUtil.simpleDateTime(this.updateTime);
    }
    return '';
  }

  // 获取格式化后的最后同步时间
  getLastSyncTimeFormatted(): string {
    if (this.lastSyncTime) {
      return DateUtil.simpleDateTime(this.lastSyncTime);
    }
    return '';
  }

  // 为前端树形组件提供value字段
  get value(): string {
    return this.id.toString();
  }

  // 为前端树形组件提供title字段
  get title(): string {
    return this.shortName || this.name;
  }

  // 获取树形节点key
  get treeKey(): string {
    return this.id.toString();
  }

  // 判断是否同步成功
  isSyncSuccess(): boolean {
    return this.syncStatus === DeptSyncStatus.SUCCESS;
  }

  // 判断是否同步失败
  isSyncFailed(): boolean {
    return this.syncStatus === DeptSyncStatus.FAILED;
  }

  // 判断是否正在同步
  isSyncing(): boolean {
    return this.syncStatus === DeptSyncStatus.SYNCING;
  }

  // 静态方法：从后端数据转换部门列表
  static convertFromBackendData(rawData: any): Dept[] {
    if (!rawData) {
      return [];
    }
    
    let records: any[] = [];
    
    // 情况1：数据本身就是数组
    if (Array.isArray(rawData)) {
      records = rawData;
    }
    // 情况2：包含 records 字段的对象
    else if (rawData.records && Array.isArray(rawData.records)) {
      records = rawData.records;
    }
    // 情况3：标准响应格式 {code: "OK", data: {...}}
    else if (rawData.code === "OK" || rawData.code === "SUCCESS") {
      if (rawData.data) {
        // 如果 data 是数组
        if (Array.isArray(rawData.data)) {
          records = rawData.data;
        }
        // 如果 data 包含 records
        else if (rawData.data.records && Array.isArray(rawData.data.records)) {
          records = rawData.data.records;
        }
      }
    }
    
    // 转换为 Dept 实例
    const depts: Dept[] = [];
    records.forEach((record, index) => {
      try {
        const dept = new Dept();
        dept.assign(record);
        depts.push(dept);
      } catch (error) {
        console.error(Lang.t('error.conversionFailed', '转换记录失败: {0}', index + 1), error, "数据:", record);
      }
    });
    
    return depts;
  }
  
  // 静态方法：构建树形结构
  static buildTree(depts: Dept[]): Dept[] {
    if (!depts || depts.length === 0) {
      return [];
    }
    
    // 创建映射表：id -> 部门
    const deptMap = new Map<number, Dept>();
    depts.forEach(dept => {
      deptMap.set(dept.id, dept);
    });
    
    // 构建树形结构
    const tree: Dept[] = [];
    depts.forEach(dept => {
      if (dept.parentId === 0 || !deptMap.has(dept.parentId)) {
        // 根节点
        tree.push(dept);
      } else {
        // 找到父节点
        const parent = deptMap.get(dept.parentId);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(dept);
        }
      }
    });
    
    // 按 orderIndex 排序
    const sortDepts = (deptList: Dept[]) => {
      deptList.sort((a, b) => a.orderIndex - b.orderIndex);
      deptList.forEach(dept => {
        if (dept.children && dept.children.length > 0) {
          sortDepts(dept.children);
        }
      });
    };
    
    sortDepts(tree);
    
    return tree;
  }
}